// Ring App Store: tenant (workspace) admin client.
//
// PATCH support: upstream's `api` helper exposes get/post/delete only,
// so PATCH calls go through `api.raw` with `method: "PATCH"`.

import { api, ApiError } from "./client";
import { unwrapEnvelope } from "@/data/unwrapEnvelope";
import type {
  RingLinkStatus,
  TenantDetails,
  TenantUpdateRequest,
} from "@/types/tenant";

export const tenantClient = {
  /** GET /api/tenant — current workspace details. */
  async get(signal?: AbortSignal): Promise<TenantDetails> {
    // Enveloped as {"tenant": {...}}; unwrap so the settings page reads flat
    // fields (returning the raw envelope blanked every field).
    const resp = await api.get<TenantDetails>("/api/tenant", { signal });
    return unwrapEnvelope(resp, "tenant");
  },

  /** PATCH /api/tenant — update display_name and/or timezone. */
  async update(body: TenantUpdateRequest, signal?: AbortSignal): Promise<TenantDetails> {
    const res = await api.raw<TenantDetails>("/api/tenant", {
      method: "PATCH",
      body,
      signal,
    });
    if (res === null) {
      throw new ApiError(0, "aborted", "request aborted", undefined, null);
    }
    return unwrapEnvelope(res.data, "tenant");
  },

  /** GET /api/tenant/ring-link-status — token rotation state. */
  getRingLinkStatus(signal?: AbortSignal): Promise<RingLinkStatus> {
    return api.get<RingLinkStatus>("/api/tenant/ring-link-status", { signal });
  },

  /** POST /api/tenant:force-relink — revokes tokens and unlinks the tenant. */
  async forceRelink(signal?: AbortSignal): Promise<void> {
    await api.post<void>("/api/tenant:force-relink", undefined, { signal });
  },
};
